import { useState, useCallback } from 'react';
import { useLazyGetPreSignedUrlQuery } from '@/redux/attachment/attachment.api.slice';

export async function getPublicFileFromS3(url: string): Promise<Response> {
  try {
    const response = await fetch(url, {
      cache: 'no-cache',
    });

    if (!response.ok) {
      throw new Error('Failed to fetch file');
    }
    return response;
  } catch (error) {
    throw new Error('Failed to fetch file');
  }
}

export async function downloadFileFromS3(
  url: string,
  fileName: string
): Promise<void> {
  try {
    const response = await getPublicFileFromS3(url);
    const blob = await response.blob();
    const link = document.createElement('a');
    const blobUrl = URL.createObjectURL(blob);

    link.href = blobUrl;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw new Error('Failed to download file');
  }
}

export const useFileDownloader = () => {
  const [fetchPreSignedUrl] = useLazyGetPreSignedUrlQuery();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  const downloadFile = useCallback(
    async (attachmentId: string, fileName: string) => {
      setLoading(true);
      setError(null);
      try {
        const preSignedUrl = await fetchPreSignedUrl({
          attachmentId,
        }).unwrap();
        if (!preSignedUrl) {
          throw new Error('Failed to fetch pre-signed URL');
        }
        const response = await fetch(preSignedUrl, {
          cache: 'no-cache',
        });

        if (!response.ok) {
          throw new Error(
            `Failed to fetch file: ${response.status} ${response.statusText}`
          );
        }

        const blob = await response.blob();
        const link = document.createElement('a');
        const blobUrl = URL.createObjectURL(blob);

        link.href = blobUrl;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (err: any) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [fetchPreSignedUrl]
  );

  return { downloadFile, loading, error };
};
